import { useState } from "react";
import { Phone, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import api, { formatApiError } from "../lib/api";

export const CallbackForm = ({ pathway, accent = "#F59E0B" }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [bestTime, setBestTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/callback", { name, phone, pathway, best_time: bestTime });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="callback-sent">
        <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="h-7 w-7 text-green-600" /></div>
        <h3 className="text-xl font-bold text-[#0F2744]">We'll be in touch soon</h3>
        <p className="mt-2 text-slate-600">Thanks {name.split(" ")[0]} — one of our volunteers will call you back as soon as we're able{bestTime ? ` (${bestTime})` : ""}, usually within a few days.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8" data-testid="callback-form-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: accent }}>
          <Phone className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#0F2744]">Prefer a quick chat?</h3>
          <p className="text-sm text-slate-500">Leave your number and one of our volunteers will call you back as soon as we're able — usually within a few days. No pressure.</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="callback-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Your name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="callback-name" />
          </div>
          <div>
            <Label className="mb-1.5 block">Phone number</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="callback-phone" />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block">Best time to call <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={bestTime} onChange={(e) => setBestTime(e.target.value)} placeholder="e.g. weekday evenings" data-testid="callback-best-time" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="callback-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-[#0F2744] hover:bg-[#0B192C] h-12" data-testid="callback-submit">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request my callback"}
        </Button>
      </form>
    </div>
  );
};
